import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Lightbox — a global click handler that opens any `<img data-lightbox>` in a
 * full-screen overlay. Also picks up sibling images inside the same
 * `data-lightbox-group="..."` container so the user can arrow-key through them.
 *
 * Just mount <Lightbox /> once at the root — no props needed. To make an image
 * lightbox-able, add `data-lightbox` (and optionally `data-lightbox-group="rental-abc"`).
 */
export default function Lightbox() {
  const [state, setState] = useState(null); // { items: [{src, alt}], index }

  const close = useCallback(() => setState(null), []);
  const next = useCallback(() => setState(s => s ? { ...s, index: (s.index + 1) % s.items.length } : s), []);
  const prev = useCallback(() => setState(s => s ? { ...s, index: (s.index - 1 + s.items.length) % s.items.length } : s), []);

  // Delegated click listener — any <img data-lightbox> opens the overlay.
  useEffect(() => {
    const onClick = (e) => {
      const img = e.target?.closest && e.target.closest("img[data-lightbox]");
      if (!img) return;
      e.preventDefault();
      const groupName = img.getAttribute("data-lightbox-group");
      let items;
      if (groupName) {
        const nodes = document.querySelectorAll(`img[data-lightbox][data-lightbox-group="${CSS.escape(groupName)}"]`);
        items = Array.from(nodes).map(n => ({ src: n.currentSrc || n.src, alt: n.alt || "" }));
      } else {
        items = [{ src: img.currentSrc || img.src, alt: img.alt || "" }];
      }
      const startSrc = img.currentSrc || img.src;
      const index = Math.max(0, items.findIndex(i => i.src === startSrc));
      setState({ items, index });
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    // Prevent body scroll while open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [state, close, next, prev]);

  if (!state) return null;
  const item = state.items[state.index];
  const showNav = state.items.length > 1;

  return (
    <div
      role="dialog" aria-modal="true"
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
      onClick={close}
      data-testid="lightbox-overlay">
      {/* Close */}
      <button
        onClick={(e)=>{ e.stopPropagation(); close(); }}
        className="absolute top-4 right-4 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center transition-colors"
        aria-label="Close" data-testid="lightbox-close">
        <X size={22}/>
      </button>

      {showNav && (
        <>
          <button
            onClick={(e)=>{ e.stopPropagation(); prev(); }}
            className="absolute left-4 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center transition-colors"
            aria-label="Previous" data-testid="lightbox-prev">
            <ChevronLeft size={24}/>
          </button>
          <button
            onClick={(e)=>{ e.stopPropagation(); next(); }}
            className="absolute right-4 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center transition-colors"
            aria-label="Next" data-testid="lightbox-next">
            <ChevronRight size={24}/>
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs" data-testid="lightbox-counter">
            {state.index + 1} / {state.items.length}
          </div>
        </>
      )}

      <img
        src={item.src}
        alt={item.alt}
        onClick={(e)=>e.stopPropagation()}
        className="max-w-[92vw] max-h-[88vh] object-contain rounded-lg"
        data-testid="lightbox-image"
      />
    </div>
  );
}
